/**
 * "FIND IN COVA CRM" (build plan 2026-11-03-002, Task 2; round 2 S7): the
 * search a row's picker runs over the firm's Cova CRM clients, so the
 * administrator (or Cova staff on the firm's instruction) can join a legacy
 * client to a client the matcher did not offer. A pick is a `handPicked` join.
 *
 * BROWSER-SAFE: `effect` only; the view groups' contracts reference these.
 *
 * IN THE KIT, NOT A LEAF: the matcher leaf runs the search and the agreement
 * leaf's two contracts publish it. A peer may reach another leaf only through
 * its server barrel, which a browser-loaded contract cannot import, so the
 * wire shapes sit here, where both leaves and the page import them by path.
 *
 * Whether another legacy record already joins a hit is worked out on the page
 * from the proposal and the drafts it already holds, never per keystroke here.
 */
import { Schema as S } from "effect"

import { searchTerm } from "./query"

/** The shortest query the search runs; below it the answer is empty and nothing is read. */
export const BOOK_SEARCH_MIN_LENGTH = 2

/** At most this many hits come back, best first (live before archived, then by name). */
export const BOOK_SEARCH_LIMIT = 20

export const BookClientHit = S.Struct({
  bookId: S.UUID,
  title: S.String,
  tradingName: S.NullOr(S.String),
  /** The ABN identifier as stored, or null. */
  abn: S.NullOr(S.String),
  archived: S.Boolean,
}).annotations({ identifier: "CutoverBookClientHit" })
export type BookClientHit = typeof BookClientHit.Type

export const BookClientHits = S.Struct({
  hits: S.Array(BookClientHit).pipe(S.maxItems(BOOK_SEARCH_LIMIT)),
}).annotations({ identifier: "CutoverBookClientHits" })
export type BookClientHits = typeof BookClientHits.Type

/**
 * The query, at most 120 characters, through the shared SearchTerm: trimmed, and a
 * term of one or two characters (or none) decodes as no search, on every lane.
 */
export const BookSearchQuery = S.Struct({
  q: searchTerm(120),
}).annotations({ identifier: "CutoverBookSearchQuery" })
export type BookSearchQuery = typeof BookSearchQuery.Type

/** "Same as another old client" (build plan Task 9): the firm's other legacy clients, by name, company name, email or code. */
export const LegacyClientHit = S.Struct({
  legacyId: S.UUID,
  title: S.String,
  companyName: S.NullOr(S.String),
  files: S.Int,
}).annotations({ identifier: "CutoverLegacyClientHit" })
export type LegacyClientHit = typeof LegacyClientHit.Type

export const LegacyClientHits = S.Struct({
  hits: S.Array(LegacyClientHit).pipe(S.maxItems(BOOK_SEARCH_LIMIT)),
}).annotations({ identifier: "CutoverLegacyClientHits" })
export type LegacyClientHits = typeof LegacyClientHits.Type
